import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const AVIFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("avi-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden" lang="en">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #495057 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
           Discover AVI Format: The Classic PC Video Workhorse
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Exploring the format that brought video to the masses in the Windows era
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-secondary fs-6 me-2 p-2">
              Classic Format
            </span>
            <span className="badge bg-light text-secondary fs-6 me-2 p-2">
              Windows Legacy
            </span>
            <span className="badge bg-light text-secondary fs-6 p-2">
              Universal Player
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-secondary border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4" aria-label="Old computer">💻</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  Remember when "video file" often meant "AVI file" on Windows computers? Before streaming and smartphones, 
                  AVI was one of the most common formats for sharing videos with friends, making home movies, and watching 
                  content on your computer. It was the reliable old pickup truck of digital video - not fancy, but it got the job done.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="alert alert-info border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5" aria-label="Light bulb">💡</div>
              <div>
                <strong>Back to basics:</strong> AVI (Audio Video Interleave) was Microsoft's answer to the 
                question "How do we put video and audio together in one file?" Introduced in 1992, it became a 
                standard way to store video on Windows computers for over a decade. Think of it as the great-grandfather 
                of modern video formats.
              </div>
            </div>
          </div>

          {/* Quick Reality Check */}
          <div className="alert alert-warning border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5" aria-label="Warning">⚠️</div>
              <div>
                <strong>Where AVI stands today:</strong> While AVI files still play on most computers, they're like 
                VHS tapes in a streaming world - they work, but modern formats do everything better. We'll help you 
                understand when to use AVI and when to choose something more modern.
              </div>
            </div>
          </div>

          {/* Quick Pros & Cons */}
          <div className="row g-4 mb-5">
            <div className="col-md-6">
              <div className="card border-success border-2 h-100">
                <div className="card-header bg-success text-white">
                  <h5 className="mb-0">
                    <span aria-label="Thumbs up">👍</span> Why We Loved AVI
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="text-success">
                    <li><strong>Wide compatibility</strong> on Windows computers</li>
                    <li><strong>Simple structure</strong> - easy to understand and work with</li>
                    <li><strong>No container royalties</strong> - free to use</li>
                    <li><strong>Editing friendly</strong> - worked well with early video editors</li>
                    <li><strong>Generally reliable</strong> - straightforward playback</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-danger border-2 h-100">
                <div className="card-header bg-danger text-white">
                  <h5 className="mb-0">
                    <span aria-label="Thumbs down">👎</span> Where AVI Showed Its Age
                  </h5>
                </div>
                <div className="card-body">
                  <ul className="text-danger">
                    <li><strong>Large file sizes</strong> - no modern compression</li>
                    <li><strong>Codec confusion</strong> - "what codec do I need?" headaches</li>
                    <li><strong>No streaming support</strong> - not designed for internet age</li>
                    <li><strong>Limited features</strong> - no chapters, menus, or rich metadata</li>
                    <li><strong>4GB file limit</strong> on older versions (though extensions exist)</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-secondary text-white py-3">
              <h2 className="h4 mb-0 fw-bold">What we'll explore together</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What made AVI so popular",
                      "The simple genius behind it", 
                      "AVI's technical foundation",
                      "The codec situation (the messy part)",
                      "Why it worked so well",
                      "Where you'll still find AVI",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-secondary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "File size realities",
                      "Modern compatibility check",
                      "When to use AVI today",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-secondary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 1"
              >
                1
              </span>
              Why AVI became so widespread (for a while)
            </h2>
            <p className="text-secondary fs-5">
              In the 1990s and early 2000s, if you had a video file on your Windows computer, it was often an AVI. 
              This format became incredibly common because it solved one simple problem: making video files that would 
              play on almost any Windows computer without special software (most of the time).
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "🔄",
                  text: "Simple Concept",
                  desc: "Just interleaved audio and video chunks",
                  alt: "Circular arrows representing interleaving"
                },
                {
                  icon: "🌐",
                  text: "Wide Compatibility", 
                  desc: "Worked with Windows Media Player out of the box",
                  alt: "Globe representing wide compatibility"
                },
                {
                  icon: "🆓",
                  text: "No Container Royalties",
                  desc: "AVI container itself was free to use",
                  alt: "Free icon representing no royalties"
                },
                {
                  icon: "🔧",
                  text: "Developer Friendly", 
                  desc: "Easy to program and work with",
                  alt: "Wrench representing developer friendly"
                },
                {
                  icon: "💾",
                  text: "CD-ROM Era Perfect",
                  desc: "Ideal for burning to CDs and sharing",
                  alt: "Floppy disk representing CD-ROM era"
                },
                {
                  icon: "📹",
                  text: "Home Video Standard",
                  desc: "Default for many early digital cameras",
                  alt: "Video camera representing home videos"
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2" aria-label={item.alt}>{item.icon}</div>
                    <div className="small fw-medium text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 2"
              >
                2
              </span>
              The simple genius of AVI: How it actually worked
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "🎵",
                  title: "Audio Video Interleave",
                  desc: "Literally means audio and video data woven together in alternating chunks",
                  alt: "Musical notes representing audio"
                },
                {
                  icon: "📦",
                  title: "RIFF Container", 
                  desc: "Based on Microsoft's Resource Interchange File Format",
                  alt: "Package representing container format"
                },
                {
                  icon: "🎯",
                  title: "Three-Part Structure", 
                  desc: "Header (hdrl), data (movi), optional index (idx1) - beautifully simple",
                  alt: "Target representing simple structure"
                },
                {
                  icon: "⚡",
                  title: "Easy Playback",
                  desc: "Players could start playing immediately without complex parsing",
                  alt: "Lightning bolt representing easy playback"
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                    <span className="fs-2 me-3" aria-label={item.alt}>{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1">{item.title}</h5>
                      <p className="text-secondary mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Thinking face">🤔</div>
                <div>
                  <strong>Think of it like this:</strong> AVI was like putting all your video frames and audio samples 
                  in a big bucket and telling the player "just grab one video chunk, then one audio chunk, and repeat." 
                  It wasn't sophisticated, but it was incredibly reliable and easy to implement in an era when every 
                  CPU cycle mattered.
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 - Technical Foundation */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 3"
              >
                3
              </span>
              AVI's technical foundation: The good, the bad, and the simple
            </h2>
            <p className="text-muted mb-4">
              <em>AVI was built on solid principles that made sense in the early 90s, but some limitations became obvious as technology advanced</em>
            </p>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="AVI technical features and limitations">
                <thead className="table-secondary">
                  <tr>
                    <th className="border-0">Feature</th>
                    <th className="border-0">How AVI Handled It</th>
                    <th className="border-0">Real-World Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      feature: "File Structure",
                      handling: "RIFF container with simple chunks",
                      impact: "Easy to program, generally reliable playback"
                    },
                    {
                      feature: "Compression",
                      handling: "Used various codecs (DivX, XviD, etc.)", 
                      impact: "Flexible but caused compatibility issues"
                    },
                    {
                      feature: "File Size Limits",
                      handling: "Original AVI: ~2-4GB, OpenDML AVI 2.0: much larger",
                      impact: "4GB wall for older files, extended for newer ones"
                    },
                    {
                      feature: "Audio Support", 
                      handling: "PCM, MP3, or other audio formats",
                      impact: "Good quality but often large file sizes"
                    },
                    {
                      feature: "Editing Workflow",
                      handling: "Popular for early video capture and editing",
                      impact: "Many early editing systems used AVI for capture"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.feature}</td>
                      <td className="text-muted">{row.handling}</td>
                      <td>{row.impact}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="alert alert-light mt-3" role="alert">
              <small>
                <strong>The 4GB wall and beyond:</strong> While original AVI files hit a ~4GB ceiling (thanks to 32-bit limits), 
                the OpenDML extension (sometimes called AVI 2.0) introduced in 1996 allowed for much larger files. This is why 
                you'd sometimes find long movies split into multiple AVI files, while others could be much larger.
              </small>
            </div>
          </div>

          {/* Section 4 - Codec Situation */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 4"
              >
                4
              </span>
              The codec situation: AVI's blessing and curse
            </h2>
            
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Video compression">🎥</span> The Flexibility
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Multiple codecs supported</strong></li>
                      <li><strong>DivX/XviD:</strong> Made decent quality possible</li>
                      <li><strong>Uncompressed:</strong> Perfect for editing</li>
                      <li><strong>DV codec:</strong> Professional quality option</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-success me-2">Flexible</span>
                      <span className="badge bg-success">Multiple Options</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Question mark">❓</span> The Codec Headache
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>"Missing codec" errors</strong> were common</li>
                      <li><strong>Different codecs</strong> on different computers</li>
                      <li><strong>Codec licensing</strong> varied by format</li>
                      <li><strong>Installation required</strong> for many users</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-warning me-2">Compatibility Issues</span>
                      <span className="badge bg-warning">User Confusion</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Solution">💡</span> Modern Solutions
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>VLC player</strong> with built-in codecs</li>
                      <li><strong>Codec packs</strong> like K-Lite (old school)</li>
                      <li><strong>Modern formats</strong> with standardized codecs</li>
                      <li><strong>Conversion tools</strong> to move to MP4</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-info me-2">Easy Playback</span>
                      <span className="badge bg-info">Better Standards</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Light bulb">💡</div>
                <div>
                  <strong>Understanding the codec landscape:</strong> While the AVI container itself had no royalty barriers, 
                  the codecs used inside (like DivX, MP3, etc.) had their own licensing situations. This flexibility was both 
                  a strength (you could choose your compression) and a weakness (compatibility nightmares).
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 - Why It Worked */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 5"
              >
                5
              </span>
              Why AVI worked so well: The magic of simplicity
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Plug and play">🔌</span> Plug & Play
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>No complex parsing</strong> needed</li>
                      <li><strong>Immediate playback</strong> start</li>
                      <li><strong>Minimal CPU usage</strong></li>
                      <li><strong>Worked on slow computers</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Tools">🛠️</span> Editing & Capture
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Early video editors</strong> loved AVI</li>
                      <li><strong>Capture cards</strong> often used AVI format</li>
                      <li><strong>Uncompressed workflows</strong> worked well</li>
                      <li><strong>Frame-accurate editing</strong> was straightforward</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-primary text-white py-3">
                    <h5 className="mb-0 fw-bold">
                      <span aria-label="Shield">🛡️</span> Reliability
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Simple structure</strong> meant fewer failures</li>
                      <li><strong>Partial files</strong> often still played</li>
                      <li><strong>Consistent behavior</strong> across systems</li>
                      <li><strong>Proven track record</strong> over years</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-warning mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Pro tip">💡</div>
                <div>
                  <strong>The editing advantage:</strong> AVI became popular in early video editing because many capture 
                  cards and editing systems used it as their native format. The straightforward structure made it ideal 
                  for the frame-accurate editing that professionals needed, even if file sizes were massive by today's standards.
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Where AVI Lives On */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 6"
              >
                6
              </span>
              Where AVI still hangs out today
            </h2>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="Current uses of AVI format">
                <thead className="table-success">
                  <tr>
                    <th className="border-0">Where You Might Find AVI</th>
                    <th className="border-0">Why It's Still Used There</th>
                    <th className="border-0">Modern Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      location: "Legacy Archives",
                      reason: "Many old video collections are in AVI format",
                      status: "Conversion recommended for preservation"
                    },
                    {
                      location: "Security Systems", 
                      reason: "Simple format works reliably for surveillance footage",
                      status: "Still in use but being phased out"
                    },
                    {
                      location: "Medical Imaging",
                      reason: "Some older medical video systems use AVI",
                      status: "Legacy systems only"
                    },
                    {
                      location: "Industrial Applications", 
                      reason: "Manufacturing and quality control systems",
                      status: "Gradual migration to modern formats"
                    },
                    {
                      location: "Personal Collections",
                      reason: "Home videos from early digital cameras",
                      status: "Worth converting to MP4 for longevity"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.location}</td>
                      <td className="text-muted">{row.reason}</td>
                      <td>
                        <span className="badge bg-warning">{row.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Professional tip">💼</div>
                <div>
                  <strong>If you find old AVI files:</strong> Think of them like photo negatives - they contain valuable memories 
                  or important content. For long-term preservation, consider converting them to modern formats like MP4 with H.264. 
                  Tools like HandBrake or FFmpeg can do this while maintaining quality, and you'll end up with smaller files that 
                  play everywhere.
                </div>
              </div>
            </div>
          </div>

          {/* Section 7 - File Size Realities */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 7"
              >
                7
              </span>
              File size realities: When bigger wasn't better
            </h2>
            <div className="row g-3">
              {[
                {
                  text: "Uncompressed AVI: A 1-minute video could be 1-2GB - massive files!",
                  alt: "Number 1"
                },
                {
                  text: "DivX compressed: Same video might be 50-100MB - much more manageable", 
                  alt: "Number 2"
                },
                {
                  text: "Modern MP4: That same video could be 10-20MB with better quality",
                  alt: "Number 3"
                },
                {
                  text: "4GB limit on older AVI: Meant HD videos often split into multiple files",
                  alt: "Number 4"
                },
                {
                  text: "Storage costs: AVI files filled up expensive hard drives quickly",
                  alt: "Number 5"
                },
                {
                  text: "Streaming impractical: File sizes made online streaming nearly impossible",
                  alt: "Number 6"
                },
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                      aria-label={tip.alt}
                    >
                      {index + 1}
                    </div>
                    <span className="fw-medium">{tip.text}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">The evolution of video file sizes (approximate for a 2-hour movie):</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul className="text-secondary">
                    <li><strong>Uncompressed AVI:</strong> 200-400GB (yes, gigabytes!)</li>
                    <li><strong>DivX AVI:</strong> 700MB-1.4GB (typical CD/DVD size)</li>
                    <li><strong>Early MP4:</strong> 300-700MB (reasonable for downloads)</li>
                    <li><strong>Modern H.265:</strong> 1-2GB (excellent quality, small size)</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="text-secondary">
                    <li><strong>Streaming today:</strong> 2-4GB for 2 hours (adaptive quality)</li>
                    <li><strong>4K HDR content:</strong> 10-20GB (high quality versions)</li>
                    <li><strong>The trend:</strong> Better quality, smaller files over time</li>
                    <li><strong>Note:</strong> Actual sizes vary by codec and settings</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-3 bg-info text-white rounded">
                <div className="d-flex">
                  <div className="me-3" aria-label="Information">ℹ️</div>
                  <div>
                    <small>
                      <strong>Perspective is everything:</strong> A single uncompressed AVI file could fill an entire hard drive 
                      from the 1990s. Today, you could store hundreds of high-quality MP4 movies in the same space. Modern 
                      compression isn't just convenient - it's fundamentally changed what's possible with digital video.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 - Modern Compatibility */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 8"
              >
                8
              </span>
              Will it play? AVI compatibility in 2024
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0">
                      <span aria-label="Check mark">✅</span> Still Plays Well Here
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Windows/Mac/Linux:</strong> VLC plays almost everything</li>
                      <li><strong>Modern media players:</strong> MPC-HC, PotPlayer, etc.</li>
                      <li><strong>Windows 10/11:</strong> Built-in players handle most AVI files</li>
                      <li><strong>Video editors:</strong> Most still import AVI without issues</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0">
                      <span aria-label="Warning">⚠️</span> Limited or Problematic
                    </h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Smartphones/tablets:</strong> Limited native support</li>
                      <li><strong>Smart TVs:</strong> Mixed support, often needs special apps</li>
                      <li><strong>Web browsers:</strong> No HTML5 video support</li>
                      <li><strong>Streaming devices:</strong> Rarely supported natively (Roku, Fire TV, etc.)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Conversion Guide */}
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">Quick AVI Conversion Guide:</h5>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold">
                      <span aria-label="Tools">🛠️</span> Using FFmpeg (Command Line)
                    </h6>
                    <code className="d-block mt-2 p-2 bg-dark text-white rounded">
                      ffmpeg -i input.avi -c:v libx264 -crf 23 -c:a aac output.mp4
                    </code>
                    <small className="text-muted d-block mt-1">Preserves quality while converting to universal MP4</small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded bg-white">
                    <h6 className="fw-bold">
                      <span aria-label="Desktop computer">💻</span> Using HandBrake (GUI)
                    </h6>
                    <p className="mb-0 small">Drag AVI file, choose "Fast 1080p30" preset, click Start</p>
                    <small className="text-muted">User-friendly with good default settings</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Globe">🌐</div>
                <div>
                  <strong>The compatibility reality:</strong> While you can probably play that old AVI file on your computer with VLC, 
                  good luck trying to watch it on your phone, tablet, or smart TV without conversion. For anything you want to share 
                  or watch on multiple devices, converting to MP4 translates your video into a universal language everyone understands.
                </div>
              </div>
            </div>
          </div>

          {/* Section 9 - When to Use AVI Today */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 9"
              >
                9
              </span>
              When should you actually use AVI today? (Spoiler: rarely)
            </h2>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm" aria-label="When to use AVI versus modern formats">
                <thead className="table-info">
                  <tr>
                    <th className="border-0">Situation</th>
                    <th className="border-0">Use AVI?</th>
                    <th className="border-0">Better Alternative & Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      situation: "New video projects",
                      useAvi: "❌ No",
                      alternative: "MP4 with H.264 or H.265 - universal compatibility"
                    },
                    {
                      situation: "Sharing with friends", 
                      useAvi: "❌ No",
                      alternative: "MP4 - works on all devices and platforms"
                    },
                    {
                      situation: "Web uploads or streaming",
                      useAvi: "❌ No", 
                      alternative: "MP4 for universal browser and platform support"
                    },
                    {
                      situation: "Legacy system requirements",
                      useAvi: "✅ If required", 
                      alternative: "Only if the specific system demands AVI format"
                    },
                    {
                      situation: "Video editing intermediate",
                      useAvi: "⚠️ Sometimes",
                      alternative: "Modern editing formats usually better, but AVI works"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.situation}</td>
                      <td>
                        <span className={row.useAvi === '❌ No' ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                          {row.useAvi}
                        </span>
                      </td>
                      <td className="text-muted">{row.alternative}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3" aria-label="Key">🔑</div>
                <div>
                  <strong>The simple rule for 2024:</strong> Use MP4 for everything unless you have a specific, compelling reason to use AVI. 
                  While MP4 can occasionally have compatibility issues with non-standard codecs, it's vastly more reliable than AVI for 
                  modern use cases. AVI had its day, but that day has passed for the vast majority of users and applications.
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
                aria-label="Section 10"
              >
                10
              </span>
              Your AVI questions answered
            </h2>
            
            <div className="accordion" id="aviFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    I found old AVI files from 2005 - are they still watchable?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#aviFAQ">
                  <div className="accordion-body">
                    <p><strong>Almost certainly yes! Here's what to expect:</strong></p>
                    <ul>
                      <li><strong>On a computer:</strong> VLC Media Player will likely play them perfectly</li>
                      <li><strong>Quality:</strong> They'll look exactly as they did in 2005 (probably 480p or less)</li>
                      <li><strong>File sizes:</strong> They'll seem huge compared to modern videos</li>
                      <li><strong>Potential issues:</strong> Some might need specific codecs, but VLC usually handles this automatically</li>
                    </ul>
                    <p><strong>My advice:</strong> Watch them in VLC, and if they're important, convert them to MP4 to future-proof them. The conversion process is quick and you'll get smaller files that play everywhere.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    Why did AVI files sometimes have those "codec not found" errors?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#aviFAQ">
                  <div className="accordion-body">
                    <p><strong>This was AVI's biggest headache. Here's why it happened:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong>The Core Problem:</strong>
                        <ul>
                          <li>AVI was just a container - like an empty box</li>
                          <li>Different "codecs" compressed the video inside</li>
                          <li>If you didn't have the right codec installed, you couldn't play the file</li>
                          <li>There were dozens of different codecs people used</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong>Modern Solutions:</strong>
                        <ul>
                          <li><strong>VLC Player:</strong> Comes with almost every codec built-in</li>
                          <li><strong>Modern formats:</strong> MP4 specifies standard codecs</li>
                          <li><strong>Conversion:</strong> Convert problematic AVI files to MP4</li>
                          <li><strong>Codec packs:</strong> K-Lite Codec Pack (the old-school solution)</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3"><strong>Today's formats largely solved this</strong> by standardizing the compression, so you rarely see "codec not found" errors with modern formats like MP4.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    What's the actual practical difference between AVI and MP4 today?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#aviFAQ">
                  <div className="accordion-body">
                    <p><strong>Think of it like the difference between a VHS tape and a Blu-ray:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong>AVI (The VHS):</strong>
                        <ul>
                          <li>Works on computers with the right software</li>
                          <li>Large file sizes, limited features</li>
                          <li>Potential codec compatibility issues</li>
                          <li>4GB file size limit on older versions</li>
                          <li>No streaming optimization</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong>MP4 (The Blu-ray):</strong>
                        <ul>
                          <li>Works on virtually all modern devices</li>
                          <li>Modern compression, small file sizes</li>
                          <li>Standardized codecs, minimal compatibility issues</li>
                          <li>Massive file size support</li>
                          <li>Streaming optimized for today's internet</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3"><strong>The bottom line:</strong> MP4 does everything AVI does, but better and more efficiently for modern use cases. It's the natural evolution of digital video formats.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    Is there any good reason I should use AVI for new projects today?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#aviFAQ">
                  <div className="accordion-body">
                    <p><strong>Honestly, almost never. But here are the tiny exceptions:</strong></p>
                    <ul>
                      <li><strong>Legacy system requirements:</strong> If you're working with ancient software or hardware that only accepts AVI</li>
                      <li><strong>Specific professional workflows:</strong> Some very niche applications might still prefer AVI for editing or capture</li>
                      <li><strong>Maximum compatibility with old computers:</strong> If you need to share with someone using Windows XP or similar</li>
                      <li><strong>Uncompressed editing:</strong> AVI can handle uncompressed video well for certain professional uses</li>
                    </ul>
                    <p><strong>For 99% of users and projects:</strong> Use MP4. It's better in every way that matters for modern video consumption, sharing, and streaming.</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-secondary">
              Working with AVI files or just curious?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Get sample AVI files to test playback, understand the format, or practice conversion.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-videos/sample-avi"
                className="btn btn-secondary btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span aria-label="Film projector">🎬</span> Get AVI Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-secondary btn-lg px-5 py-3"
              >
                <span aria-label="Book">📖</span> Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various AVI types, codecs, and quality levels - perfect for testing compatibility or learning about this classic format.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default AVIFormatGuide;

//                      "Your AVI questions answered",
